/**
 * The admin's console theme as a value (spec docs/superpowers/specs/2026-09-27-admin-console-themes-design.md,
 * "The catalogue"): validation, the text the hf-admin-ui cookie holds, and light or dark for a
 * mode. Pure — no DOM, no cookie — so the server layouts, the first-paint script and the client
 * all read a choice the same way.
 *
 * The rule everywhere: a value is a choice only when every part of it is a catalogue value — the
 * theme (a ready-made one or Custom), the mode and, when it carries one, the builder's settings,
 * whose own colour must be exactly six hex digits. Anything else reads as DEFAULT_ADMIN_UI
 * (classic / system), whole. The cookie is text the browser sends and it is embedded into the
 * first-paint script, so nothing else it might hold may pass through.
 *
 * The cookie's text:
 *   `<theme>.<mode>`                                            the builder at its defaults
 *   `<theme>.<mode>.<color>.<hex>.<style>.<sidebar>.<recent>`   otherwise
 * where `<hex>` is six hex digits or empty and `<recent>` is hex digits joined by `-`, or empty:
 * "custom.dark.custom.0ea5e9.mono.inset.0ea5e9-ff8800". A choice is always written in the shortest
 * form that holds it, so two choices are the same exactly when their texts are.
 */
import {
  ADMIN_CUSTOM_COLORS,
  ADMIN_CUSTOM_OWN_COLOR,
  ADMIN_CUSTOM_RECENT_MAX,
  ADMIN_CUSTOM_SIDEBARS,
  ADMIN_CUSTOM_STYLES,
  ADMIN_MODES,
  ADMIN_THEMES,
  ADMIN_THEME_CHOICES,
  DEFAULT_ADMIN_CUSTOM,
  DEFAULT_ADMIN_UI,
  type AdminCustomColor,
  type AdminCustomSidebar,
  type AdminCustomStyle,
  type AdminCustomTheme,
  type AdminMode,
  type AdminThemeChoice,
  type AdminThemeKey,
  type AdminUi,
} from './admin-themes'

/** The query the device answers for "System". */
export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

/** The admin's own colour: exactly six hex digits, nothing around them. */
const HEX = /^[0-9a-fA-F]{6}$/

const isOneOf =
  <T extends string>(list: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === 'string' && (list as readonly string[]).includes(value)

export const isAdminThemeKey: (value: unknown) => value is AdminThemeKey = isOneOf(ADMIN_THEMES)
export const isAdminThemeChoice: (value: unknown) => value is AdminThemeChoice = isOneOf(ADMIN_THEME_CHOICES)
export const isAdminMode: (value: unknown) => value is AdminMode = isOneOf(ADMIN_MODES)
const isCustomColor: (value: unknown) => value is AdminCustomColor = isOneOf(ADMIN_CUSTOM_COLORS)
const isCustomStyle: (value: unknown) => value is AdminCustomStyle = isOneOf(ADMIN_CUSTOM_STYLES)
const isCustomSidebar: (value: unknown) => value is AdminCustomSidebar = isOneOf(ADMIN_CUSTOM_SIDEBARS)

/** Six hex digits, either case — the only shape the admin's own colour may take. */
export const isAdminCustomHex = (value: unknown): value is string => typeof value === 'string' && HEX.test(value)

const defaultUi = (): AdminUi => ({ theme: DEFAULT_ADMIN_UI.theme, mode: DEFAULT_ADMIN_UI.mode })
const defaultCustom = (): AdminCustomTheme => ({ ...DEFAULT_ADMIN_CUSTOM, recent: [] })

const isDefaultCustom = (c: AdminCustomTheme): boolean =>
  c.color === DEFAULT_ADMIN_CUSTOM.color &&
  c.hex === DEFAULT_ADMIN_CUSTOM.hex &&
  c.style === DEFAULT_ADMIN_CUSTOM.style &&
  c.sidebar === DEFAULT_ADMIN_CUSTOM.sidebar &&
  c.recent.length === 0

/**
 * The builder's settings from anything — lower-cased, and only its five fields — or null when any
 * part is not a catalogue value: a colour outside the grid, the own colour without a hex, a hex or
 * a recent colour that is not six hex digits, the same recent colour twice, more of them than the
 * builder keeps. A missing hex reads as none, missing recent colours as none.
 */
export function adminCustomFrom(value: unknown): AdminCustomTheme | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const { color, hex, style, sidebar, recent } = value as Record<string, unknown>
  if (!isCustomColor(color) && color !== ADMIN_CUSTOM_OWN_COLOR) return null
  if (hex !== undefined && hex !== null && !isAdminCustomHex(hex)) return null
  if (color === ADMIN_CUSTOM_OWN_COLOR && !isAdminCustomHex(hex)) return null
  if (!isCustomStyle(style) || !isCustomSidebar(sidebar)) return null
  const list: unknown = recent ?? []
  if (!Array.isArray(list) || list.length > ADMIN_CUSTOM_RECENT_MAX || !list.every(isAdminCustomHex)) return null
  const colours = (list as string[]).map((c) => c.toLowerCase())
  if (new Set(colours).size !== colours.length) return null
  return {
    color,
    hex: isAdminCustomHex(hex) ? hex.toLowerCase() : null,
    style,
    sidebar,
    recent: colours,
  }
}

/**
 * A builder read field by field (review A6, 2026-09-27) — the API's adminUiCustomRead: each field
 * that is the catalogue's is kept, each one that is not reads as its default. The own colour
 * without a usable hex is no own colour; recent colours keep only six-hex-digit entries,
 * lower-cased, once each, at most the builder's eight. Null when it is no builder at all.
 */
export function adminCustomRead(value: unknown): AdminCustomTheme | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const { color, hex, style, sidebar, recent } = value as Record<string, unknown>
  const ownHex = isAdminCustomHex(hex) ? hex.toLowerCase() : null
  return {
    color: isCustomColor(color) ? color : color === ADMIN_CUSTOM_OWN_COLOR && ownHex ? ADMIN_CUSTOM_OWN_COLOR : 'default',
    hex: ownHex,
    style: isCustomStyle(style) ? style : 'default',
    sidebar: isCustomSidebar(sidebar) ? sidebar : 'default',
    recent: Array.isArray(recent) ? [...new Set(recent.filter(isAdminCustomHex).map((c) => c.toLowerCase()))].slice(0, ADMIN_CUSTOM_RECENT_MAX) : [],
  }
}

/**
 * An API answer or anything else as a choice, read part by part (review A6, 2026-09-27): the
 * theme, the mode and each builder field fall back to their own default when they are not the
 * catalogue's — a broken builder no longer resets the admin's theme and mode — and a builder at its
 * defaults is left out. Only those fields, never anything else the value carries.
 *
 * Part by part because the API merges saves field by field (admin-ui-patch.ts): a choice read
 * all-or-nothing would make every save of another field read back as lost. The cookie is still
 * read whole (parseAdminUi): it is our own text, and anything else in it was not written by us.
 */
export function adminUiFrom(value: unknown): AdminUi {
  const record = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
  const theme = isAdminThemeChoice(record.theme) ? record.theme : DEFAULT_ADMIN_UI.theme
  const mode = isAdminMode(record.mode) ? record.mode : DEFAULT_ADMIN_UI.mode
  const builder = adminCustomRead(record.custom)
  return builder && !isDefaultCustom(builder) ? { theme, mode, custom: builder } : { theme, mode }
}

/** True when `value` is a complete choice (not merely something adminUiFrom can read). */
export const isAdminUi = (value: unknown): value is AdminUi => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const { theme, mode, custom } = value as Record<string, unknown>
  return isAdminThemeChoice(theme) && isAdminMode(mode) && (custom === undefined || custom === null || adminCustomFrom(custom) !== null)
}

/** The builder's settings in a choice — a fresh copy, its defaults when the choice carries none. */
export function customOf(ui: AdminUi): AdminCustomTheme {
  const builder = ui.custom ? adminCustomFrom(ui.custom) : null
  return builder ?? defaultCustom()
}

/** The cookie's text as a choice. Anything that is not exactly what serializeAdminUi writes — classic / system. */
export function parseAdminUi(value: string | undefined | null): AdminUi {
  if (typeof value !== 'string') return defaultUi()
  const parts = value.split('.')
  if (parts.length === 2) {
    const [theme, mode] = parts
    return isAdminThemeChoice(theme) && isAdminMode(mode) ? { theme, mode } : defaultUi()
  }
  if (parts.length !== 7) return defaultUi()
  const [theme, mode, color, hex, style, sidebar, recent] = parts
  if (!isAdminThemeChoice(theme) || !isAdminMode(mode)) return defaultUi()
  const builder = adminCustomFrom({ color, hex: hex === '' ? null : hex, style, sidebar, recent: recent === '' ? [] : recent.split('-') })
  if (!builder) return defaultUi()
  return isDefaultCustom(builder) ? { theme, mode } : { theme, mode, custom: builder }
}

/** A choice as the cookie's text: "ledger.dark", "custom.dark.blue..mono.inset.". A value outside the catalogue is written as the default. */
export function serializeAdminUi(ui: AdminUi): string {
  const { theme, mode, custom } = adminUiFrom(ui)
  if (!custom) return `${theme}.${mode}`
  return [theme, mode, custom.color, custom.hex ?? '', custom.style, custom.sidebar, custom.recent.join('-')].join('.')
}

/** Two choices are the same when every part is — the builder's included. */
export const sameAdminUi = (a: AdminUi, b: AdminUi): boolean => serializeAdminUi(a) === serializeAdminUi(b)

/** Light or dark for a mode; System follows the device. */
export function resolveAdminMode(mode: AdminMode, deviceIsDark: boolean): 'light' | 'dark' {
  if (mode === 'light' || mode === 'dark') return mode
  return deviceIsDark ? 'dark' : 'light'
}

/** Whether the device prefers dark — false where there is no window or no matchMedia. */
export function deviceIsDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia(SYSTEM_DARK_QUERY).matches
  } catch {
    return false
  }
}

/**
 * Is this path the console: `/admin` and everything under it (the sign-in pages included)?
 * Exact-or-slash, so `/administrator` or `/admin-settings` are not.
 */
export function isAdminConsolePath(pathname: string | null | undefined): boolean {
  return typeof pathname === 'string' && (pathname === '/admin' || pathname.startsWith('/admin/'))
}
